import { set as vueSet } from 'vue'
import { omit, pick, isEqual, clamp } from 'lodash-es'
import { get, set, del } from 'idb-keyval'
import datasets, { datasetInstance } from '~/data/datasets.js'
import { deserializeFrame } from '~/data/dataframes'
import { TEMPLATE_WIDTH, TEMPLATE_HEIGHT } from '~/utils/create-helpers'

const IDB_STORE = 'mint-autosave'
const defaultState = () => ({
  frames: [],
  augmintable: {
    contentType: 'image/jpeg',
    image: '',
  },
  editHistory: [],
  redoHistory: [],
  mostRecentSelection: {},
  copiedStyle: {},
})

export const state = defaultState

export const getters = {
  augmintable: (state) => state.augmintable,
  frames: (state) => state.frames,
  selectedFrames: (state) => state.frames.filter((frame) => frame.selected),
  mostRecentSelection: (state) => state.mostRecentSelection,
  currentHistoryState: (state) => ({
    augmintable: pick(state.augmintable, ['contentType', 'image']),
    frames: state.frames.map((frame) => frame.createSerialize()),
  }),
  mintData: (state) => {
    if (state.augmintable?.image && state.augmintable.contentType) {
      return {
        amountOwned: 1, // Hack so the buy prompt doesn't show up
        image: state.augmintable.image,
        contentType: state.augmintable.contentType,
        tags: getTags(state.frames),
        frames: state.frames.map((frame) => frame.mintSerialize()),
      }
    }
    return null
  },
}

function setState(state, newState) {
  if (newState) {
    console.info('restoring state', newState)
    Object.entries(newState).forEach(([key, value]) => {
      if (key === 'frames') {
        state.frames = value.map(deserializeFrame)
      } else {
        vueSet(state, key, value)
      }
    })
  }
}

export const mutations = {
  setState,
  resetState(state) {
    Object.assign(state, defaultState())
  },
  undo(state) {
    console.info('undo')
    state.redoHistory.push(state.editHistory.pop())
    setState(state, state.editHistory.at(-1))
  },
  redo(state) {
    console.info('redo')
    setState(state, state.redoHistory.pop())
  },
  selectAll(state) {
    state.frames.forEach((frame) => (frame.selected = true))
  },
  unselectAll(state) {
    state.frames.forEach((frame) => (frame.selected = false))
  },
  setAugmintable(state, augmintable) {
    state.augmintable = augmintable
  },
  // Frames operated on immutably to avoid a deep watcher
  addFrames(state, frames) {
    state.frames = [...state.frames, ...frames]
  },
  deleteFrame(state, idx) {
    state.frames = state.frames.filter((_, i) => i !== idx)
  },
  deleteSelected(state) {
    state.frames = state.frames.filter((frame) => !frame.selected)
  },
  deleteFrameIds(state, ids) {
    state.frames = state.frames.filter((frame) => !ids.includes(frame.id))
  },
  setSelected(state, { frame, selected }) {
    frame.selected = selected
  },
  setMostRecentSelection(state, frame) {
    state.mostRecentSelection = frame
  },
  copyStyle(state) {
    state.copiedStyle = {
      ...omit(state.mostRecentSelection.styles, ['top', 'bottom', 'left', 'right']),
    }
  },
  historyPush(state, historyItem) {
    state.editHistory.push(historyItem)
  },
  pasteStyle(state) {
    state.frames.forEach((frame) => {
      if (!frame.selected) return
      Object.entries(state.copiedStyle).forEach(([key, value]) => {
        vueSet(frame.styles, key, value)
      })
    })
  },
  updateFrameStyles(state, { frame, styles }) {
    Object.assign(frame.styles, styles)
  },
  updateFramePosition(state, { frame, position }) {
    Object.assign(frame.position, omit(position, ['x', 'y']))
    if (!isNaN(position.x)) frame.position.x = clamp(position.x, 0, TEMPLATE_WIDTH)
    if (!isNaN(position.y)) frame.position.y = clamp(position.y, 0, TEMPLATE_HEIGHT)
  },
  updateFrameArgs(state, { frame, args }) {
    Object.assign(frame.args, args)
  },
}

export const actions = {
  updateSelected({ commit, getters }, { styles, position, args }) {
    if (getters.selectedFrames.length !== 1) {
      throw new Error('updateSelected can only be called with single selection')
    }

    const frame = getters.selectedFrames[0]
    if (styles) {
      commit('updateFrameStyles', { frame, styles })
    }
    if (position) {
      commit('updateFramePosition', { frame, position })
    }
    if (args) {
      commit('updateFrameArgs', { frame, args })
    }
  },
  toggleSelectAll({ getters, state, commit }) {
    if (getters.selectedFrames.length === state.frames.length) {
      commit('unselectAll')
    } else {
      commit('selectAll')
    }
  },
  setSelected({ commit }, { frame, selected }) {
    commit('setSelected', { frame, selected })

    if (selected) {
      commit('setMostRecentSelection', frame)
    }
  },
  toggleSelected({ dispatch }, frame) {
    return dispatch('setSelected', { frame, selected: !frame.selected })
  },
  setSelectedFrames({ commit, state }, selectedFrames) {
    const selectedIds = selectedFrames.map(({ id }) => id)
    state.frames.forEach((frame) => {
      const selected = selectedIds.includes(frame.id)
      commit('setSelected', { frame, selected })
    })
    if (selectedFrames.length) {
      commit('setMostRecentSelection', selectedFrames[0])
    }
  },
  saveState({ commit, getters }) {
    commit('historyPush', getters.currentHistoryState)
    set(IDB_STORE, getters.currentHistoryState)
  },
  async restoreSavedState({ commit }) {
    const savedState = await get(IDB_STORE)
    commit('setState', savedState)
  },
  addDataset({ commit, dispatch }, datasetId) {
    const newFrames = datasetInstance(datasetId).frames
    commit('addFrames', newFrames)
    dispatch('setSelectedFrames', newFrames)
    return newFrames
  },
  addFrame({ commit, dispatch }, frame) {
    commit('addFrames', [frame])
    dispatch('setSelectedFrames', [frame])
  },
  resetSave({ commit }) {
    del(IDB_STORE)
    commit('resetState')
  },
}

function getTags(frames) {
  if (frames.length) {
    const simplifyDatasetForComparison = (frames) =>
      frames.map((frame) => ({
        ...pick(frame, ['type', 'args']),
        styles: pick(frame.styles, ['top', 'bottom', 'left', 'right']),
      }))

    const matchedDataset = datasets.find((dataset) =>
      isEqual(
        simplifyDatasetForComparison(dataset.frames),
        simplifyDatasetForComparison(frames)
      )
    )
    if (matchedDataset) {
      return [...matchedDataset.tags]
    } else {
      return [...new Set(frames.flatMap((frame) => frame.tags()))]
    }
  } else {
    return ['data set 0']
  }
}
